/**
 * Hero Carousel Module
 * Auto-rotating feature carousel with indicator navigation
 */

import { trackCTAClick } from './gtm-events.js';

const ICONS = {
  // Pro page icons
  calendar: '<svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor"><path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11zM9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm-8 4H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z"/></svg>',
  clock: '<svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>',
  folder: '<svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor"><path d="M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10z"/></svg>',
  inbox: '<svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5v-3h3.56c.69 1.19 1.97 2 3.45 2s2.75-.81 3.45-2H19v3zm0-5h-4.99c0 1.1-.9 2-2 2s-2-.9-2-2H5V5h14v9z"/></svg>',
  chart: '<svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor"><path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z"/></svg>',
  // Care page icons
  file: '<svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>',
  bell: '<svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor"><path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/></svg>',
  bank: '<svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor"><path d="M4 10h3v7H4v-7zm6.5 0h3v7h-3v-7zM2 19h20v3H2v-3zm15-9h3v7h-3v-7zm-5-9L2 6v2h20V6l-10-5z"/></svg>',
  heart: '<svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>'
};

// Color palettes for different page types
const PRO_COLORS = ['#1890ff', '#52c41a', '#ff6f2c', '#fa8c16', '#f5222d'];
// Care colors - Brand Orange (Option A) palette with Cool Grey background
// The 5th color (heart icon) is Brand Orange to match Care branding
const CARE_COLORS = ['#e86025', '#52c41a', '#1890ff', '#fa8c16', '#e86025'];

const ROTATION_INTERVAL = 4000;

export function initHeroCarousel() {
  const carousel = document.querySelector('[data-hero-carousel]');
  if (!carousel) return;

  // Get messages data from script tag
  const dataScript = document.getElementById('hero-messages-data');
  if (!dataScript) return;

  let messages;
  try {
    messages = JSON.parse(dataScript.textContent);
  } catch (e) {
    console.error('Failed to parse hero messages data');
    return;
  }

  const iconEl = carousel.querySelector('[data-feature-icon]');
  const labelEl = carousel.querySelector('[data-feature-label]');
  const headlineEl = carousel.querySelector('[data-feature-headline]');
  const detailEl = carousel.querySelector('[data-feature-detail]'); // Optional - may be removed
  const indicators = carousel.querySelectorAll('[data-feature-indicator]');

  // Only require core elements - detail is optional
  if (!iconEl || !labelEl || !headlineEl) return;

  // Detect if this is a Care page carousel (teal theme)
  const isCareCarousel = carousel.classList.contains('hero-section__features--care');
  const COLORS = isCareCarousel ? CARE_COLORS : PRO_COLORS;

  let currentIndex = 0;
  let intervalId = null;

  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function updateFeature(index) {
    // Defensive: ensure index is within bounds
    const safeIndex = index % messages.length;
    const message = messages[safeIndex];

    // Defensive: ensure message exists
    if (!message) return;

    // Use modulo to cycle through colors (they have fixed length of 5)
    const colorIndex = safeIndex % COLORS.length;
    const color = COLORS[colorIndex];

    // Get icon from message data if available, otherwise fallback to index-based
    const iconKey = message.icon || ['calendar', 'clock', 'folder', 'inbox', 'chart'][safeIndex % 5];
    const icon = ICONS[iconKey];

    // Defensive: only update if we have valid data
    if (icon) {
      iconEl.innerHTML = icon;
      iconEl.style.backgroundColor = `${color}15`;
      iconEl.style.color = color;
    }

    if (message.label) {
      labelEl.textContent = message.label;
      labelEl.style.color = color;
    }

    if (message.headline) {
      headlineEl.textContent = message.headline;
    }

    // Update detail only if element exists (optional element)
    if (detailEl && message.detail) {
      detailEl.textContent = message.detail;
    }

    // Update indicators
    indicators.forEach((indicator, i) => {
      if (i === safeIndex) {
        indicator.classList.add('hero-section__indicator--active');
        indicator.style.borderColor = COLORS[i % COLORS.length];
        indicator.style.color = COLORS[i % COLORS.length];
      } else {
        indicator.classList.remove('hero-section__indicator--active');
        indicator.style.borderColor = '';
        indicator.style.color = '';
      }
    });

    currentIndex = safeIndex;
  }

  function nextFeature() {
    const nextIndex = (currentIndex + 1) % messages.length;
    updateFeature(nextIndex);
  }

  function startRotation() {
    if (prefersReducedMotion) return;
    intervalId = setInterval(nextFeature, ROTATION_INTERVAL);
  }

  function stopRotation() {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  }

  // Click handlers for indicators
  indicators.forEach(indicator => {
    indicator.addEventListener('click', () => {
      const index = parseInt(indicator.getAttribute('data-feature-indicator'), 10);
      updateFeature(index);

      // Track indicator click in GTM
      const message = messages[index];
      const featureLabel = message?.label || `Feature ${index + 1}`;
      trackCTAClick(featureLabel, 'hero_carousel_indicator');

      // Reset interval after manual navigation
      stopRotation();
      startRotation();
    });
  });

  // Start auto-rotation
  startRotation();
}
